import { Injectable, Optional } from '@angular/core';
import { TranslateService } from '@itonics/ngx-translate-simple';
import { BareboneMentionable, ElementMentionables, Mentionables, ReferencedElementType } from '../interfaces/ui-quill-mention.interfaces';
import { EnrichMentionPipe } from '../pipes/enrich-mention.pipe';
import { UiQuillMentionContract } from './ui-quill-mention.contract';

@Injectable({
  providedIn: null
})
export class UiQuillMentionService {
  // Add this into the quill config
  public readonly quillMentionConfig = {
    mention: {
      allowedChars: /^[A-Za-z\sÅÄÖåäö0-9]*$/,
      mentionDenotationChars: ['@', '#'],
      source: async (searchTerm, renderList, mentionChar) => {
        const matchedPeople = await this.suggestMentions(searchTerm, mentionChar);
        renderList(matchedPeople, searchTerm);
      },
      renderItem: this.mentionContract ? this.mentionContract.renderMentionableItem : (item, searchTerm) => item.value,
      // WARNING: renderLoading Works properly with quill-mention@3.0.2 only.
      // due to https://github.com/quill-mention/quill-mention/commit/dc6954213fb6bf406a6ed4f1dc758d7924b6ab53 merged since 3.0.3
      // What it causes? infinite loop due to aria update on quill.
      renderLoading: () => this.translate.instant('mention.loading'),
      dataAttributes: [
        'id',
        'value',
        'denotationChar',
        'link',
        'target',
        'blotAlternativeIconHtml',
        'blotTooltip',
        'blotClickable',
        'blotCustomClasses'
      ]
    }
  };

  private readonly noContractWarn = 'Mention Contract is not defined. Please check if it is provided so the mentions can work.';

  private options: { disableUserMentions?: boolean; disableElementMentions?: boolean } = {};

  constructor(@Optional() private readonly mentionContract: UiQuillMentionContract, private readonly translate: TranslateService) {}

  public init(mentionOptions?: { disableUserMentions?: boolean; disableElementMentions?: boolean }) {
    if (!this.mentionContract) {
      // If mentionContract is not provided, we simple warn and disable mention
      console.warn(this.noContractWarn);
      this.options = { disableElementMentions: true, disableUserMentions: true };
      return;
    }
    if (mentionOptions) {
      this.options = mentionOptions;
    }
    if (!this.options.disableElementMentions) {
      // Just calling this to prefetch the cache for things. It reduces waiting time for mentions dropdown.
      const prefetch = this.mentionContract.allThings;
    }
  }

  // Internal function to get mentions from the quill output.
  public async getMentions(content: { [key: string]: any }): Promise<Array<BareboneMentionable>> {
    if (!this.mentionContract) {
      console.warn(this.noContractWarn);
      return [];
    }
    const context = await this.mentionContract.getContext();
    return content.ops
      .filter((op) => op.insert.mention)
      .map((op) => ({
        objectSpaceUri: context.space,
        objectTenantUri: context.tenant,
        objectType: op.insert.mention.denotationChar === '@' ? ReferencedElementType.USER : ReferencedElementType.ENTITY,
        objectUri: op.insert.mention.id
      }));
  }

  // Internal function to sanitize mentions from the quill output before post.
  public sanitizeMentions(content: { [key: string]: any }): { [key: string]: any } {
    content.ops.forEach((op) => {
      if (op.insert.mention) {
        // For each mentions, we only preserve denotationChar and id to be stored with the Quill json
        op.insert.mention = {
          denotationChar: op.insert.mention.denotationChar,
          id: op.insert.mention.id,
          value: ''
        } as Mentionables;
      }
    });
    return content;
  }

  /**
   * Check whether the provided id is accessible for mention or not
   *
   */
  public isAccessible(id: string): boolean {
    return !(EnrichMentionPipe.inaccessiblePeople.has(id) || EnrichMentionPipe.inaccessibleThings.has(id));
  }

  public async suggestMentions(searchTerm: string, type: '@' | '#'): Promise<Array<Mentionables>> {
    if (!this.mentionContract) {
      // If no contract, no need to suggest anything
      console.warn(this.noContractWarn);
      return [];
    }
    let cache: ElementMentionables;
    switch (type) {
      case '@':
        if (this.options.disableUserMentions) {
          return [];
        }
        const users = await this.mentionContract.getUsersBySearchText(searchTerm);
        return users.filter((user: Mentionables) => this.isAccessible(user.id));
      case '#':
        if (this.options.disableElementMentions) {
          return [];
        } else {
          cache = searchTerm.length ? await this.mentionContract.fetchSearched(searchTerm) : await this.mentionContract.allThings;
        }
        // Call anyway so that filter can reset, this shouldn't toggle any refetch
        // But should be replaced once we have lazy loading which quill-mention still lacks
        this.mentionContract.fetchSearched(searchTerm || '');
        break;
    }

    // Provide filtered mentionables as suggestion based on searchTerm
    return Array.from(cache.values()).filter((item: Mentionables) => {
      const isAccessibleForMention = this.isAccessible(item.id);

      const valueInLowerCase = item.value.toLowerCase();
      const searchTermInLowerCase = searchTerm.toLowerCase();

      const isitemSearched =
        // First we check the value
        valueInLowerCase.includes(searchTermInLowerCase) ||
        // Then we check if the item is user mention and check different field values
        (item.user &&
          (item.user.firstName.toLowerCase().includes(searchTermInLowerCase) ||
            item.user.lastName.toLowerCase().includes(searchTermInLowerCase) ||
            item.user.email.toLowerCase().includes(searchTermInLowerCase)));

      // We don't suggest already mentioned item.
      return isAccessibleForMention && isitemSearched;
    });
  }
}
